import { DetailPageLayout } from '@app/DetailPageLayout';

import React, { useMemo, useState } from 'react';
import {
  Column,
  IOption,
  RecordsNotFound,
  ResponsivePaginatedTable,
  Select,
  createTableCell,
  createTableColumn,
} from '@gcl/react-core';
import { createTableDefaultColumn } from '@common/tableColumn.services';
import { useGetProjectDataAllAsync } from '@project/project.queries';
import { LabelWithMessage } from '@common/LabelWithMessage';
import { IProject } from '@project/project.models';
import { TableContentLoader } from '@common/TableContentLoader';
import { routes } from '@routes/routes.models';
import { useNavigate } from '@routes/routes.hooks';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { IMobileActionUsage } from './report.models';
import { useGetLoadingRemovalCertsMobileActionUsageAsync } from './report.queries';

export const ReportT4MobileUsage = () => {
  const { navigate } = useNavigate();
  const [selectedProject, setSelectedProject] = useState<IOption<IProject>>();
  const {
    data: dataProjectAll,
    isLoading: isLoadingProjectAll,
    isRefetching: isRefetchingProjectAll,
    error: errorProjectAll,
  } = useGetProjectDataAllAsync();

  const {
    data: dataMobileActionUsage,
    isInitialLoading: isLoadingMobileActionUsage,
    isRefetching: isRefetchinMobileActionUsage,
    error: errorMobileActionUsage,
  } = useGetLoadingRemovalCertsMobileActionUsageAsync({ projectId: selectedProject?.data?.id ?? 0 });

  const projectOptions: IOption<IProject>[] = useMemo(() => {
    return (
      dataProjectAll?.items?.map((project) => {
        return {
          data: project,
          text: `${project.projectNo} - ${project.projectName}`,
          key: project.id,
        };
      }) ?? []
    );
  }, [JSON.stringify(dataProjectAll)]);

  const t4MobileUsageTableColumns: Column[] = React.useMemo(() => {
    return [
      createTableDefaultColumn<IMobileActionUsage>(
        'actionMonth',
        'Month',
        undefined,
        undefined,
        undefined,
        false,
        true,
      ),
      createTableColumn({
        Header: 'TWC Approval T4 in Mobile (Overall Count)',
        accessor: 'id',
        disableResizing: true,
        disableSortBy: true,
        Cell: createTableCell<string, any>(
          (value: string, item: IMobileActionUsage, index: number, cellExtras: { [key: string]: any }) => {
            return (
              <>
                {item.mobileTwcCount}({item.totalTwcCount})
              </>
            );
          },
        ),
      } as Column),
      createTableColumn({
        Header: 'SRP Save / Init T4 in Mobile',
        accessor: 'id1',
        disableResizing: true,
        disableSortBy: true,
        Cell: createTableCell<string, any>(
          (value: string, item: IMobileActionUsage, index: number, cellExtras: { [key: string]: any }) => {
            return <>{item.mobileSrpSaveCount + item.mobileSrpInitCount}</>;
          },
        ),
      } as Column),
      createTableColumn({
        Header: 'SRP Init T4 in Mobile (Overall Count)',
        accessor: 'id2',
        disableResizing: true,
        disableSortBy: true,
        Cell: createTableCell<string, any>(
          (value: string, item: IMobileActionUsage, index: number, cellExtras: { [key: string]: any }) => {
            return (
              <>
                {item.mobileSrpInitCount}({item.totalSrpInitCount})
              </>
            );
          },
        ),
      } as Column),
    ];
  }, []);
  return (
    <DetailPageLayout
      isGlobalPage
      errorGetDetail={null}
      isDrawer={false}
      isRestricted={false}
      data="placeholder"
      isLoading={isLoadingProjectAll}
      isRefetching={isRefetchingProjectAll}
      title="T4 Mobile Usage"
      breadcrumb={[
        {
          text: 'Home',
          prefixIconName: 'House',
          onClick: () => {
            navigate(routes.home.path);
          },
        },
        {
          text: 'Reports',
          prefixIconName: 'Clipboard2Data',
          onClick: () => navigate(routes.report.main.path),
        },
        {
          text: 'T4 Mobile Usage',
          prefixIconName: 'Clipboard2Data',
          active: true,
        },
      ]}
      render={() => {
        return (
          <>
            <ApiErrorDisplay
              errors={[
                { error: errorMobileActionUsage, genericError: 'Get mobile action usage failed' },
                { error: errorProjectAll, genericError: 'Get project options failed' },
              ]}
            />
            <LabelWithMessage labelFor="mobile-usage-project" text="Project" />
            <Select
              className="gcl-mb-md"
              id="mobile-usage-project"
              onChange={(newValue: IOption<IProject>[]) => {
                setSelectedProject(newValue[0]);
              }}
              value={selectedProject ? [selectedProject] : undefined}
              options={projectOptions}
              allowSearch={false}
              clearable={false}
              multiple={false}
            />
            <TableContentLoader isLoading={isLoadingMobileActionUsage || isRefetchinMobileActionUsage}>
              {dataMobileActionUsage && (
                <ResponsivePaginatedTable
                  disablePagination
                  allowSelectRow={false}
                  columns={t4MobileUsageTableColumns}
                  items={dataMobileActionUsage ?? []}
                />
              )}
              {!dataMobileActionUsage && <RecordsNotFound />}
            </TableContentLoader>
          </>
        );
      }}
    />
  );
};
